import { inject, Injectable } from '@angular/core';
import en from '../../assets/i18n/en.json';
import es from '../../assets/i18n/es.json';
import { SessionStateService } from '@abp/ng.core';
import { AngularGridInstance, Column } from 'angular-slickgrid';
import { RESIZE_CONFIG } from './consts/data-table-helper.config';

@Injectable()
export class DataTableHelperService {
  private readonly sessionState = inject(SessionStateService);
  private grid: AngularGridInstance;
  private lastColumnId: string;
  private hasBeenResized: boolean = false;
  private numberInputKeydownHandler?: (event: KeyboardEvent) => void;
  private numberInputListenerTarget?: HTMLElement;

  getGridLanguage() {
    const language = this.sessionState.getLanguage();
    const currentLang = language?.split('-')[0] || 'en';

    if (currentLang === 'es') {
      return es;
    } else {
      return en;
    }
  }

  getHasBeenResized() {
    return this.hasBeenResized;
  }

  setGridInstance(instance: AngularGridInstance) {
    this.grid = instance;
  }

  attachNumberInputExponentBlock(): void {
    if (!this.grid?.slickGrid) return;

    const container =
      (this.grid.slickGrid as any).getContainerNode?.() ||
      this.grid.slickGrid.getCanvasNode()?.parentElement;

    if (!container || this.numberInputListenerTarget) return;

    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLInputElement | null;
      if (!target || target.tagName !== 'INPUT') return;
      if (target.type !== 'number') return;

      if (event.key === 'e' || event.key === 'E') {
        event.preventDefault();
      }
    };

    container.addEventListener('keydown', handler, true);
    this.numberInputKeydownHandler = handler;
    this.numberInputListenerTarget = container;
  }

  detachNumberInputExponentBlock(): void {
    if (this.numberInputListenerTarget && this.numberInputKeydownHandler) {
      this.numberInputListenerTarget.removeEventListener('keydown', this.numberInputKeydownHandler, true);
      this.numberInputListenerTarget = undefined;
      this.numberInputKeydownHandler = undefined;
    }
  }

  private setLastColumnId(columns: Column[]) {
    this.lastColumnId = columns[columns.length - 1]?.id as string;
  }

  resizeColumnByContent(columnId: string): void {
    const grid = this.grid.slickGrid;
    const columns = grid.getColumns();
    this.setLastColumnId(columns);

    const columnIndex = columns.findIndex(col => col.id === columnId);

    if (columnIndex === -1) {
      return;
    }

    const column = columns[columnIndex];
    const newWidth = this.calculateColumnWidth(column);

    column.width = newWidth;

    this.applyColumnChanges(columns);
  }

  resizeColumnsByContent(): void {
    const grid = this.grid.slickGrid;
    const columns = grid.getColumns();
    this.setLastColumnId(columns);

    columns.forEach(column => {
      const newWidth = this.calculateColumnWidth(column);
      column.width = newWidth;
    });

    this.applyColumnChanges(columns);
  }

  resizeLastColumn(id: string) {
    if(!this.hasBeenResized || !this.lastColumnId) return;

    this.resizeColumnByContent(this.lastColumnId);
    this.resizeColumnByContent(id)
  }

  private calculateColumnWidth(column: Column): number {
    const grid = this.grid.slickGrid;
    const dataView = this.grid.dataView;

    const MAX_ROWS_TO_CHECK = RESIZE_CONFIG.maxRowsToCheck;
    const PADDING = RESIZE_CONFIG.padding;
    const MIN_WIDTH = RESIZE_CONFIG.minWidth;
    const MAX_WIDTH = RESIZE_CONFIG.maxWidth;
    const LAST_COLUMN_EXTRA_PADDING = RESIZE_CONFIG.lastColumnExtraPadding;

    if (
      column.id === '_checkbox_selector' ||
      column.id === '_edit' ||
      column.id === '_read' ||
      column.resizable === false ||
      !column.field
    ) {
      return column.width || MIN_WIDTH;
    }

    const items = dataView.getItems();
    const rowsToCheck = Math.min(items.length, MAX_ROWS_TO_CHECK);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    const gridCanvas = grid.getCanvasNode();
    const computedStyle = window.getComputedStyle(gridCanvas);
    context.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`;

    let maxWidth = 0;

    const headerText = column.name || '';
    const headerWidth = context.measureText(headerText.toString()).width + PADDING;
    maxWidth = Math.max(maxWidth, headerWidth);

    for (let i = 0; i < rowsToCheck; i++) {
      const item = items[i];
      if (!item) continue;

      let cellValue = item[column.field];

      if (column.formatter && typeof column.formatter === 'function') {
        try {
          cellValue = column.formatter(i, 0, cellValue, column, item, grid);
          if (typeof cellValue === 'string') {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cellValue;
            cellValue = tempDiv.textContent || tempDiv.innerText || '';
          }
        } catch (e) {
        }
      }

      const textValue = cellValue != null ? String(cellValue) : '';
      const textWidth = context.measureText(textValue).width + PADDING;
      maxWidth = Math.max(maxWidth, textWidth);
    }
    let finalWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, maxWidth));
    const isLastColumn = this.lastColumnId === column.id;

    if (isLastColumn) {
      finalWidth += LAST_COLUMN_EXTRA_PADDING;
    }

    return finalWidth;
  }

  private applyColumnChanges(columns: Column[]): void {
    const grid = this.grid.slickGrid;

    grid.setOptions({ forceFitColumns: false });
    grid.setColumns(columns);
    grid.invalidate();
    grid.render();
    
    this.hasBeenResized = true;
  }
}
